import React from 'react'
import clsx from 'clsx'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { TableCell } from '@material-ui/core'
import { AutoSizer, Column, Table, TableCellProps, TableHeaderProps } from 'react-virtualized'

declare module '@material-ui/core/styles/withStyles' {
  // Augment the BaseCSSProperties so that we can control jss-rtl
  interface BaseCSSProperties {
    /*
     * Used to control if the rule-set should be affected by rtl transformation
     */
    flip?: boolean
  }
}

const useStyle = makeStyles((theme: Theme) => ({
  table: {
    // temporary right-to-left patch, waiting for
    // https://github.com/bvaughn/react-virtualized/issues/454
    '& .ReactVirtualized__Table__headerRow': {
      borderBottom: '1px solid rgba(224, 224, 224, 1)',
      flip: false,
      paddingRight: theme.direction === 'rtl' ? '0px !important' : undefined
    }
  },
  tableRow: {
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box'
  },

  tableCellContainer: {
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box'
  },
  tableCell: {
    boxSizing: 'border-box',
    display: 'block',
    flex: 1
  },
  tableHeaderCell: {
    // border: 'solid black 1px'
    fontWeight: 'bold',
    whiteSpace: 'nowrap'
  }
}))

type ColumnType = {
  dataKey: string
  label: string
  align?: 'left' | 'center' | 'right'
  width: number
}

const MuiVirtualizedTable: React.FC<{
  columns: ColumnType[]
  headerHeight?: number
  rowHeight?: number
  onRowClick?: () => void
  rowGetter: (info: { index: number }) => any
  rowCount: number
}> = ({ columns, headerHeight = 100, rowHeight = 100, onRowClick, rowGetter, rowCount }) => {
  const classes = useStyle({})

  /**
   * テーブルヘッダー
   * @param label Columnで指定したlabelオプションの値が入っていくる
   */
  const headerRenderer = ({ label, columnIndex }: TableHeaderProps & { columnIndex: number }) => {
    return (
      <TableCell
        component="div"
        className={clsx(classes.tableCell, classes.tableHeaderCell)}
        variant="head"
        align={columns[columnIndex].align || 'left'}
      >
        {label}
      </TableCell>
    )
  }

  /**
   * テーブルボディ
   * @param param0
   */
  const cellRenderer = ({ cellData, columnIndex }: TableCellProps) => {
    return (
      <TableCell
        component="div"
        className={classes.tableCell}
        variant="body"
        style={{ height: rowHeight }}
        align={
          columnIndex != null && columns[columnIndex].align ? columns[columnIndex].align : 'left'
        }
      >
        {cellData}
      </TableCell>
    )
  }

  return (
    <AutoSizer>
      {({ height, width }) => (
        <Table
          height={height}
          width={width}
          headerHeight={headerHeight}
          rowHeight={rowHeight}
          gridStyle={{
            direction: 'inherit'
          }}
          className={classes.table}
          rowClassName={classes.tableRow}
          rowGetter={rowGetter}
          rowCount={rowCount}
          // headerRowRenderer={headerRowRenderer}
        >
          {columns.map((column, index) => {
            return (
              <Column
                key={column.dataKey}
                dataKey={column.dataKey}
                className={classes.tableCellContainer}
                label={column.label}
                width={column.width}
                headerRenderer={headerProps => {
                  return headerRenderer({
                    ...headerProps,
                    columnIndex: index
                  })
                }}
                cellRenderer={cellProps => {
                  return cellRenderer(cellProps)
                }}
              />
            )
          })}
        </Table>
      )}
    </AutoSizer>
  )
}

export default MuiVirtualizedTable
